// @flow
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Helmet } from 'react-helmet-async';

import { Container } from '@finect/tabular-core/Container';
import { Inner } from '@finect/tabular-core/Inner';
import { LiveBanner } from '@finect/tabular-components/LiveBanner';
import { MainContent } from '@finect/tabular-core/MainContent';
import { Small } from '@finect/tabular-core/Small';
import { Paragraph } from '@finect/tabular-core/Paragraph';
import { BottomAds } from '@finect/tabular-components/Ads';
import { H1 } from '@finect/tabular-core/Headings';

import { Search } from '@finect/tabular-icons/search';
import { Datalayer } from '@finect/front-gtm';
import { followTag, unfollowTag, getTags } from '../../store/tags/actions';
import type { UserType, TagType, StoreType } from '../../types';
import { processTags } from '../../utils';
import { Navigation, Heading } from '../../components';
import { TopicsInner, TopicSearch, TopicSearchInput, Topic, FollowingFilter } from './partials';

const mapStateToProps = (state: StoreType) => ({
  user: state.user.user,
  tags: state.tags
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      followTag,
      unfollowTag,
      getTags
    },
    dispatch
  )
});

type Props = {
  tags: {
    [key: string]: TagType
  },
  user: UserType,
  actions: Object,
  history: Object
};

type State = {
  filteredTags: Array<string>,
  followingFilter: boolean
};

class Topics extends React.Component<Props, State> {
  searchInput: HTMLInputElement;
  following: HTMLInputElement;

  state = {
    filteredTags: [],
    followingFilter: false
  };

  static loadInitialData(store, cookie) {
    return store.dispatch(getTags(cookie));
  }

  componentDidMount() {
    if (!Object.keys(this.props.tags).length) {
      this.props.actions.getTags();
    }
  }

  follow = (id: number) => {
    this.props.actions.followTag(id);
  };

  unFollow = (id: number) => {
    this.props.actions.unfollowTag(id);
  };

  goBack = () => {
    this.props.history.goBack();
  };

  cleanTags = () => {
    setTimeout(() => this.setState({ filteredTags: [] }), 0);
  };

  filterTags = () => {
    if (!this.searchInput) return null;
    const value = this.searchInput.value;
    if (value.replace(/\s/g, '') === '') {
      return this.cleanTags();
    }
    const { tags } = this.props;
    const { followingFilter } = this.state;
    const allTags = followingFilter ? processTags(tags).followed : processTags(tags).sorted;
    const searchTerm = value.toLowerCase();
    const filteredTags = [];

    allTags.map((tag) => {
      const name = tag.name.toLowerCase();
      if (name.includes ? name.includes(searchTerm) : name.indexOf(searchTerm) > -1) {
        filteredTags.push(tag.id);
      }
    });

    this.setState({ filteredTags });
  };

  toggleFilter = () => {
    setTimeout(
      () =>
        this.setState(
          state => ({ followingFilter: !state.followingFilter }),
          () => this.filterTags()
        ),
      0
    );
  };

  render() {
    const { fromApp, backPath } = this.props.history.location.state || {};
    const { tags, user } = this.props;
    const { filteredTags, followingFilter } = this.state;
    const hasTags = Object.keys(tags).length !== 0;

    let followedTags = [];
    let sortedTags = [];

    if (hasTags) {
      const allTags = processTags(tags);
      followedTags = allTags.followed;
      sortedTags = allTags.sorted;
    }

    const currentTags = followingFilter ? followedTags : sortedTags;

    const isSearching = this.searchInput && this.searchInput.value !== '';

    return (
      <Container>
        <Helmet>
          <meta charSet="utf-8" />
          <title>Temas - Preguntas Finect</title>
          <meta name="description" content="¿Tienes dudas sobre tus finanzas? - Todas las respuestas en Finect" />
          <meta name="robots" content="noindex, follow" />
        </Helmet>

        <Datalayer id="Scene" type="ready" params={{ pageType: 'questionsTopics' }} />
        <Datalayer id="Scene" type="pageType" params={{ pageType: 'questionsTopics' }} />

        <LiveBanner />

        <Heading>
          <Navigation fromApp={fromApp} backPath={backPath} goBack={this.goBack} />
        </Heading>
        <Inner>
          <MainContent>
            <H1>Todos los temas</H1>
            <TopicSearch noFilter={!user.isLoggedIn}>
              <Search size="16" strokeWidth="2.4" />
              <TopicSearchInput
                // $FlowFixMe
                ref={el => (this.searchInput = el)}
                type="search"
                placeholder="Busca un tema"
                onChange={this.filterTags}
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
              />
              {user.isLoggedIn ? (
                <FollowingFilter inputRef={el => (this.following = el)} handleChange={this.toggleFilter} />
              ) : null}
            </TopicSearch>
            {isSearching ?
              filteredTags.length ? (
                <TopicsInner wrap="wrap">
                  {filteredTags.map((tagKey) => {
                    const tag = tags[tagKey];
                    return (
                      <Topic
                        key={tag.id}
                        tag={tag}
                        action={tag.following ? this.unFollow : this.follow}
                        isLoggedIn={user.isLoggedIn}
                      />
                    );
                  })}
                </TopicsInner>
              ) : (
                <Paragraph textCenter center>
                  <Small light>
                    {`No ${followingFilter ? 'sigues' : 'hay'} ningún tema que coincida con tu búsqueda.`}
                  </Small>
                </Paragraph>
              )
              : followingFilter && !currentTags.length ? (
                <Paragraph textCenter center>
                  <Small light>{`Vaya, parece que aún no sigues ningún tema.`}</Small>
                </Paragraph>
              ) : (
                <TopicsInner wrap="wrap">
                  {currentTags.map(tag => (
                    <Topic
                      key={tag.id}
                      tag={tag}
                      action={tag.following ? this.unFollow : this.follow}
                      isLoggedIn={user.isLoggedIn}
                    />
                  ))}
                </TopicsInner>
              )}
          </MainContent>
        </Inner>

        <BottomAds path="Finect_Content_1" sticky closeable />
      </Container>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Topics);
